import axios from 'axios';
import { destroyCookie, parseCookies, setCookie } from 'nookies';
import { EnvVars } from 'env';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

export function setupAPIClient(ctx = undefined) {
  let cookies = parseCookies(ctx);
  const token = cookies['meunascimento.token'];

  const api = axios.create({
    baseURL: EnvVars.API_URL,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (err) => {
      cookies = parseCookies(ctx);
      const { 'meunascimento.refreshToken': refreshToken } = cookies;
      const originalRequest = err.config;
      if (err.response?.status === 401 && err.response?.data?.message === 'token expired') {
        const response = await api.put('/auth', {
          refresh_token: refreshToken,
        });
        const { accessToken } = response.data;
        setCookie(ctx, 'meunascimento.token', accessToken, {
          maxAge: 60 * 60 * 24 * 30, // 30 days
          path: '/',
        });

        setCookie(ctx, 'meunascimento.refreshToken', response.data.refreshToken, {
          maxAge: 60 * 60 * 24 * 30, // 30 days
          path: '/',
        });

        api.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
        return api({
          ...originalRequest,
          headers: { ...originalRequest.headers, Authorization: `Bearer ${accessToken}` },
        });
      }
      if (typeof window === 'undefined') {
        destroyCookie(undefined, 'meunascimento.token');
        destroyCookie(undefined, 'meunascimento.refreshToken');
      }
      // Mensagem amigável para exibir ao usuário (evita "Request failed with status code 400")
      (err as { userMessage?: string }).userMessage = getApiErrorMessage(err);
      return Promise.reject(err);
    },
  );
  return api;
}

export async function subscribeToEmailMarketing(userData: {
  name: string;
  email: string;
  cpf: string;
  phone: string;
}, context: 'vaquinha' | 'register' = 'vaquinha') {
  try {
    const cleanData = {
      name: userData.name.trim(),
      email: userData.email.trim().toLowerCase(),
      cpf: userData.cpf.replace(/[^\d]/g, ''),
      phone: userData.phone.replace(/[^\d]/g, ''),
    };

    if (!cleanData.name || cleanData.name.length < 2) {
      throw new Error('Nome deve ter pelo menos 2 caracteres');
    }
    if (!cleanData.email || !cleanData.email.includes('@')) {
      throw new Error('Email inválido');
    }
    if (!cleanData.cpf || cleanData.cpf.length !== 11) {
      throw new Error('CPF deve ter 11 dígitos');
    }
    if (!cleanData.phone || cleanData.phone.length < 10) {
      throw new Error('Telefone deve ter pelo menos 10 dígitos');
    }

    const listIds = context === 'vaquinha' ? ['vaquinha'] : ['register'];

    const dataFormats = [
      {
        ...cleanData,
        listIds: listIds,
      },
      {
        ...cleanData,
        document: cleanData.cpf,
        document_type: 'CPF',
        phone: cleanData.phone,
        listIds: listIds,
      },
      {
        nome: cleanData.name,
        email: cleanData.email,
        cpf: cleanData.cpf,
        telefone: cleanData.phone,
        listIds: listIds,
      },
      {
        name: cleanData.name,
        email: cleanData.email,
        cpf: cleanData.cpf,
        phone: cleanData.phone,
        source: 'website',
        consent: true,
        marketing: true,
        listIds: listIds,
      },
      {
        name: cleanData.name,
        email: cleanData.email,
        document: cleanData.cpf,
        document_type: 'CPF',
        phone: cleanData.phone,
        source: 'website',
        consent: true,
        marketing: true,
        created_at: new Date().toISOString(),
        listIds: listIds,
      },
      {
        name: cleanData.name,
        email: cleanData.email,
        cpf: cleanData.cpf,
        phone: cleanData.phone,
        listIds: [...listIds, 'newsletter'],
        source: 'website',
        consent: true,
      }
    ];

    for (let i = 0; i < dataFormats.length; i++) {
      try {
        try {
          const response = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/mail-marketing/subscribe`, dataFormats[i]);
          return response.data;
        } catch (error: any) {
          const api = setupAPIClient();
          const response = await api.post('/mail-marketing/subscribe', dataFormats[i]);
          return response.data;
        }
      } catch (error: any) {
        if (i === dataFormats.length - 1) {
          throw error;
        }
      }
    }
  } catch (error: any) {
    throw error;
  }
}